import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { Btn } from '../components';
import { fieldProps } from '../lib/a11y';
import { palette as T, alpha, type } from '../design';

const labelStyle = { display: 'block', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, marginBottom: 6 };
const fieldErrorStyle = { fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: '6px 0 0' };
const inputStyle = (invalid) => ({
  width: '100%', padding: '11px 14px', borderRadius: 12, border: '1px solid ' + (invalid ? T.errorRed : T.border),
  fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', boxSizing: 'border-box',
});

function shannonEntropy(str) {
  const len = str.length;
  if (len === 0) return 0;
  const freq = {};
  for (const ch of str) {
    freq[ch] = (freq[ch] || 0) + 1;
  }
  let entropy = 0;
  for (const count of Object.values(freq)) {
    const p = count / len;
    entropy -= p * Math.log2(p);
  }
  return entropy * len;
}

function getPasswordStrength(password) {
  if (!password || password.length < 8) return { score: 0, label: 'Too short', color: T.errorRed };
  const entropy = shannonEntropy(password);
  if (entropy < 30) return { score: 1, label: 'Weak', color: T.errorRed };
  if (entropy < 45) return { score: 2, label: 'Fair', color: T.caramelGold };
  if (entropy < 60) return { score: 3, label: 'Good', color: T.sageGreen };
  return { score: 4, label: 'Strong', color: T.sageDark };
}

export function RegisterPage({ setPage }) {
  const { register, resendVerification } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // `error` is the server's answer, form-level. `fieldErrors` are the form's own checks, each read
  // out with its field (#345, DS-3).
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  // Name and email keep the browser's own required and format checks.
  const nameField = fieldProps('register-name', '');
  const emailField = fieldProps('register-email', '');
  const passwordField = fieldProps('register-password', fieldErrors.password);
  const confirmField = fieldProps('register-confirm-password', fieldErrors.confirmPassword);

  const clearFieldError = (name) => setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));

  // Shows one field's problem and moves focus to that field, so it is read out with its error.
  const failField = (name, id, message) => {
    setFieldErrors({ [name]: message });
    document.getElementById(id)?.focus();
  };

  const strength = useMemo(() => getPasswordStrength(password), [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    if (password.length < 8) {
      failField('password', passwordField.inputProps.id, 'Your password must be at least 8 characters.');
      return;
    }
    if (strength.score < 2) {
      failField('password', passwordField.inputProps.id, 'Password is too weak. Try a longer or more varied passphrase.');
      return;
    }
    if (password !== confirmPassword) {
      failField('confirmPassword', confirmField.inputProps.id, 'Those passwords do not match. Please retype them.');
      return;
    }
    setLoading(true);
    try {
      // Register creates student accounts only; instructors join through the application (ADR 0004).
      await register(email, password, name);
      setRegisteredEmail(email);
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!registeredEmail) return;
    setResendLoading(true);
    try {
      await resendVerification(registeredEmail);
      showSnackbar('Verification email resent.', { variant: 'success' });
    } catch (err) {
      showSnackbar(err.message || 'Failed to resend.', { variant: 'error' });
    } finally {
      setResendLoading(false);
    }
  };

  if (registeredEmail) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', background: T.ivory }}>
        <div style={{ width: '100%', maxWidth: 420, background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '32px 28px', textAlign: 'center' }}>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.espresso, margin: '0 0 12px' }}>Check your email</h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut, marginBottom: 20 }}>
            We sent a verification link to <strong>{registeredEmail}</strong>. Please click the link to verify your account before logging in.
          </p>
          <button
            onClick={handleResend}
            disabled={resendLoading}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.terracotta, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, padding: 0, marginBottom: 20 }}
          >
            {resendLoading ? 'Sending…' : 'Resend verification email'}
          </button>
          <div>
            <button onClick={() => setPage('login')} style={{ background: T.terracotta, color: T.white, border: 'none', borderRadius: 12, padding: '12px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, cursor: 'pointer' }}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', background: T.ivory }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 32, color: T.espresso, margin: '0 0 8px' }}>Create your account</h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut }}>Start your language learning journey</p>
        </div>

        <form onSubmit={handleSubmit} style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '32px 28px' }}>
          {error && (
            <div role="alert" style={{ background: alpha('errorRed', 'tint'), border: '1px solid ' + alpha('errorRed', 'selection'), borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>{error}</p>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label {...nameField.labelProps} style={labelStyle}>Full name</label>
            <input
              {...nameField.inputProps}
              type="text"
              autoComplete="name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Jaime Reyes"
              required
              style={inputStyle(false)}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label {...emailField.labelProps} style={labelStyle}>Email</label>
            <input
              {...emailField.inputProps}
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={inputStyle(false)}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label {...passwordField.labelProps} style={labelStyle}>Password</label>
            <input
              {...passwordField.inputProps}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e => { setPassword(e.target.value); clearFieldError('password'); }}
              placeholder="Min. 8 characters"
              required
              minLength={8}
              style={inputStyle(fieldErrors.password)}
            />
            {fieldErrors.password && <p {...passwordField.errorProps} style={fieldErrorStyle}>{fieldErrors.password}</p>}
            {password.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: T.warmGrey, overflow: 'hidden' }}>
                    <div style={{ width: `${(strength.score / 4) * 100}%`, height: '100%', background: strength.color, borderRadius: 2, transition: 'all 0.2s' }} />
                  </div>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, fontWeight: 600, color: strength.color }}>{strength.label}</span>
                </div>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: 0 }}>
                  Use 8+ characters. Avoid common words.
                </p>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label {...confirmField.labelProps} style={labelStyle}>Confirm password</label>
            <input
              {...confirmField.inputProps}
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); clearFieldError('confirmPassword'); }}
              placeholder="Re-enter your password"
              required
              minLength={8}
              style={inputStyle(fieldErrors.confirmPassword)}
            />
            {fieldErrors.confirmPassword && <p {...confirmField.errorProps} style={fieldErrorStyle}>{fieldErrors.confirmPassword}</p>}
          </div>

          <Btn style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
            {loading ? 'Creating account…' : 'Create account →'}
          </Btn>

          <p style={{ textAlign: 'center', marginTop: 20, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>
            Already have an account?{' '}
            <button type="button" onClick={() => setPage('login')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.terracotta, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, padding: 0 }}>
              Sign in
            </button>
          </p>
        </form>

        <p style={{ textAlign: 'center', marginTop: 20, marginBottom: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>
          <a
            href="#/apply-to-teach"
            onClick={(e) => { e.preventDefault(); setPage('apply-to-teach'); }}
            style={{ color: T.terracotta, fontWeight: 600, textDecoration: 'none' }}
          >
            Want to teach? Apply to become an instructor →
          </a>
        </p>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <button onClick={() => setPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>← Back to home</button>
        </div>
      </div>
    </div>
  );
}
